"use client";

import React, { useEffect, useState, useContext } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useNavigate } from "react-router-dom";
import { createMRTColumnHelper } from "material-react-table";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import GenericTable from "../genericTable.js";
import { AuthContext } from "context";
import apiService from "../../services/api-service";
import toast from "react-hot-toast";
import HighQualityIcon from "@mui/icons-material/HighQuality";

const PianoControlli = () => {
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState([]);
	const [wbsData, setWbsData] = useState([]);
	const authContext = useContext(AuthContext);
	const navigate = useNavigate();
	const columnHelper = createMRTColumnHelper();

	const handleSetNotification = (message, type) => {
		if (type === "success") toast.success(message);
		else if (type === "error") toast.error(message);
		else if (type === "warning") toast.warning(message);
	};

	const fetchData = () => {
		setLoading(true);
		Promise.all([
			apiService.get(
				"/piano-controlli/",
				{ area_id: localStorage.getItem("areaSelected") },
				{}
			),
			apiService.get(
				"/jobs-management/",
				{
					options: true,
					area_id: localStorage.getItem("areaSelected"),
				},
				{}
			),
		])
			.then(([pianoControlliResponse, wbsResponse]) => {
				setData(pianoControlliResponse);
				// Filter only job options and convert to usable format
				const wbsJobs = (wbsResponse || [])
					.filter((item) => item.id.includes("-job"))
					.map((item) => ({
						id: parseInt(item.id.replace("-job", "")),
						name: item.name,
					}));
				setWbsData(wbsJobs);
			})
			.catch((error) => {
				console.error("Error fetching data:", error);
				handleSetNotification(
					"Errore nel caricamento dei dati",
					"error"
				);
				// Fallback to empty arrays
				setData([]);
				setWbsData([]);
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		fetchData();
	}, []);

	const handleEdit = (id) => navigate(`/piano-controlli/edit/${id}`);

	const handleDelete = async (id) => {
		if (
			window.confirm(
				"Sei sicuro di voler eliminare questo controllo?\nAttenzione questa operazione è irreversibile."
			)
		) {
			try {
				await apiService.delete(`/piano-controlli/`, { id: id }, {});
				handleSetNotification(
					"Controllo eliminato con successo",
					"success"
				);
				fetchData();
			} catch (error) {
				console.error("Delete error:", error);
				handleSetNotification(
					"Errore durante l'eliminazione del controllo",
					"error"
				);
			}
		}
	};

	const handleFileChange = (event) => {
		if (!event.target.files) return;

		toast.loading("Aggiornamento Piano dei Controlli in corso...", {
			id: "piano-controlli-upload-toast",
		});

		const file = event.target.files[0];
		if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
			handleSetNotification("Per favore carica un file CSV", "error");
			return;
		}

		const reader = new FileReader();
		reader.onload = (e) => {
			const base64Content = e.target.result.split(",")[1];

			apiService
				.post(
					"/piano-controlli/upload-csv/",
					{},
					{
						data: base64Content,
						area_id: localStorage.getItem("areaSelected"),
						name: file.name,
					}
				)
				.then((response) => {
					handleSetNotification(
						"Piano dei Controlli aggiornato con successo",
						"success"
					);
					fetchData();
				})
				.catch((error) => {
					handleSetNotification(
						"Errore durante l'aggiornamento del Piano dei Controlli. Riprova più tardi se l'errore persiste contatta il supporto tecnico indicato l'errore #00086",
						"error"
					);
				});
		};

		reader.onerror = () => {
			handleSetNotification(
				"Errore durante la lettura del file. Riprova più tardi se l'errore persiste contatta il supporto tecnico indicato l'errore #00087",
				"error"
			);
		};

		reader.readAsDataURL(file);
	};

	const getWbsName = (wbsId) => {
		const wbs = wbsData.find((item) => item.id === wbsId);
		return wbs ? wbs.name : "N/A";
	};

	const columns = [
		columnHelper.accessor("wbs_id", {
			header: "WBS",
			size: 200,
			enableSorting: true,
			Cell: ({ cell }) => (
				<Typography variant="body2">
					{getWbsName(cell.getValue())}
				</Typography>
			),
		}),
		columnHelper.accessor("controlli", {
			header: "Controlli",
			size: 300,
			Cell: ({ cell }) => {
				const controlli = cell.getValue();
				if (Array.isArray(controlli)) {
					return (
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								gap: 1,
							}}
						>
							{controlli.map((controllo, index) => (
								<Typography key={index} variant="body2">
									{controllo}
								</Typography>
							))}
						</Box>
					);
				}
				return (
					<Typography variant="body2">
						{controlli || "N/A"}
					</Typography>
				);
			},
		}),
		columnHelper.accessor("riferimenti", {
			header: "Riferimenti",
			size: 200,
			enableSorting: true,
		}),
		columnHelper.accessor("registrazione", {
			header: "Registrazione",
			size: 200,
			enableSorting: true,
		}),
		columnHelper.accessor("applicabile", {
			header: "Applicabile",
			size: 100,
			Cell: ({ cell }) => {
				const isApplicabile = cell.getValue();
				return (
					<Typography
						variant="body2"
						sx={{
							color: isApplicabile ? "green" : "red",
							fontWeight: "bold",
						}}
					>
						{isApplicabile ? "Sì" : "No"}
					</Typography>
				);
			},
		}),
	];

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Box sx={{ padding: "20px" }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						marginBottom: "24px",
					}}
				>
					<Typography
						variant="h4"
						sx={{
							fontWeight: 600,
							color: "#333",
							fontSize: "1.75rem",
							display: "flex",
							alignItems: "center",
							gap: "12px",
						}}
					>
						Piano dei Controlli
					</Typography>

					<Box sx={{ display: "flex", gap: 2 }}>
						<Button
							component="label"
							variant="contained"
							startIcon={<CloudUploadIcon />}
							sx={{
								textTransform: "none",
								borderRadius: "8px",
								color: "white!important",
							}}
						>
							CARICA CSV
							<input
								type="file"
								accept=".csv"
								hidden
								onChange={handleFileChange}
							/>
						</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={() => navigate("/piano-controlli/add")}
							sx={{
								textTransform: "none",
								borderRadius: "8px",
								color: "white!important",
							}}
						>
							Aggiungi Controllo
						</Button>
					</Box>
				</Box>

				{loading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "300px",
						}}
					>
						<CircularProgress
							size={120}
							sx={{ color: "#2b84ea" }}
						/>
					</Box>
				) : (
					<GenericTable
						authModelName="DAILYACTIVITY"
						data={data}
						columns={columns}
						onEdit={handleEdit}
						onDelete={handleDelete}
						sortingKey="wbs_id"
					/>
				)}
			</Box>
		</DashboardLayout>
	);
};

export default PianoControlli;
